/** Header interactions: sticky styling, search dialog, mobile navigation and cart count. */
(function () {
if (window.RaidageHeaderInitialized) return;
window.RaidageHeaderInitialized = true;

class Header {
  constructor(header) {
    this.header = header;
    this.section = header.closest('.shopify-section') || header.parentElement || document;
    this.searchOpen = header.querySelector('[data-search-open]');
    this.mobileMenuOpen = header.querySelector('[data-mobile-menu-open]');
    this.mobileSearchOpen = this.section.querySelector('[data-mobile-search-open]');
    this.megaItems = [];
    this.openMegaItem = null;
    this.mobileHeaderStates = null;
    this.destroyed = false;
    this.listeners = [];
    this.dialogs = [];
    this.isHomepage = document.body.classList.contains('template-index');
    this.init();
  }

  listen(target, type, handler, options) {
    if (!target) return;
    target.addEventListener(type, handler, options);
    this.listeners.push([target, type, handler, options]);
  }

  init() {
    this.handleScroll = this.handleScroll.bind(this);
    this.listen(window, 'scroll', this.handleScroll, { passive: true });
    this.handleScroll();
    this.setupMegaMenu();
    this.setupSearch();
    this.setupMobileMenu();
    this.listen(document, 'raidage:overlay-open', () => this.closeMegaMenu());
    this.updateCartCount();
  }

  handleScroll() {
    this.header.classList.toggle('is-scrolled', window.scrollY > 50);
  }

  setupSearch() {
    const searchDialog = this.section.querySelector('[data-search-overlay]');
    if (!this.searchOpen || !searchDialog || !window.RaidageDialog) return;

    const dialog = new window.RaidageDialog(searchDialog, {
      trigger: this.searchOpen,
      initialFocus: searchDialog.querySelector('input[type="search"]'),
      onOpen: () => {
        this.closeMegaMenu();
        this.updateSearchViewport(searchDialog);
      },
      onClose: () => {
        const predictive = searchDialog.querySelector('predictive-search');
        if (predictive) {
          predictive.abortController?.abort();
          predictive.abortController = new AbortController();
          predictive.close();
        }
      },
      onAfterClose: () => {
        searchDialog.style.removeProperty('--raidage-viewport-height');
        searchDialog.style.removeProperty('--raidage-viewport-top');
      },
    });
    this.dialogs.push(dialog);
    const updateViewport = () => {
      if (dialog.active) this.updateSearchViewport(searchDialog);
    };
    this.listen(window, 'resize', updateViewport, { passive: true });
    if (window.visualViewport) {
      this.listen(window.visualViewport, 'resize', updateViewport, { passive: true });
      this.listen(window.visualViewport, 'scroll', updateViewport, { passive: true });
    }
  }

  updateSearchViewport(searchDialog) {
    const viewport = window.visualViewport;
    searchDialog.style.setProperty('--raidage-viewport-height', `${viewport?.height || window.innerHeight}px`);
    searchDialog.style.setProperty('--raidage-viewport-top', `${viewport?.offsetTop || 0}px`);
  }

  setupMobileMenu() {
    const mobileMenu = this.section.querySelector('[data-mobile-menu-overlay]');
    if (!this.mobileMenuOpen || !mobileMenu || !window.RaidageDialog) return;

    const dialog = new window.RaidageDialog(mobileMenu, {
      trigger: this.mobileMenuOpen,
      initialFocus: mobileMenu.querySelector('a, button'),
      toggleTrigger: true,
      extraFocusable: [this.mobileMenuOpen],
      inertExceptions: [this.header],
      closeDuration: 250,
      onOpen: () => {
        this.closeMegaMenu();
        this.positionMobileMenu(mobileMenu);
        this.setMobileHeaderInactive(true);
      },
      onClose: () => this.setMobileHeaderInactive(false),
    });
    this.dialogs.push(dialog);
    this.listen(mobileMenu, 'click', (event) => {
      if (event.target === mobileMenu) dialog.close();
    });

    const searchDialog = this.dialogs.find((item) => item.element.matches('[data-search-overlay]'));
    if (this.mobileSearchOpen && searchDialog) {
      this.listen(this.mobileSearchOpen, 'click', (event) => {
        event.preventDefault();
        dialog.close({ immediate: true, restoreFocus: false });
        searchDialog.open(this.mobileSearchOpen, this.mobileMenuOpen);
      });
    }

    this.listen(window, 'resize', () => {
      if (window.matchMedia('(min-width: 769px)').matches) {
        dialog.close({ immediate: true, restoreFocus: false });
      } else if (dialog.active) {
        this.positionMobileMenu(mobileMenu);
      }
    }, { passive: true });
  }

  positionMobileMenu(mobileMenu) {
    const bottom = this.header.getBoundingClientRect().bottom;
    mobileMenu.style.setProperty('--raidage-menu-top', `${Math.max(0, Math.min(window.innerHeight, bottom))}px`);
  }

  setMobileHeaderInactive(inactive) {
    if (inactive) {
      if (this.mobileHeaderStates) return;
      this.mobileHeaderStates = ['.header__center', '.header__right-group']
        .map((selector) => this.header.querySelector(selector))
        .filter(Boolean)
        .map((element) => ({ element, inert: element.inert }));
      this.mobileHeaderStates.forEach(({ element }) => { element.inert = true; });
    } else if (this.mobileHeaderStates) {
      this.mobileHeaderStates.forEach(({ element, inert }) => { element.inert = inert; });
      this.mobileHeaderStates = null;
    }
  }

  setupMegaMenu() {
    const nav = this.header.querySelector('[data-mega-nav]');
    if (!nav) return;

    this.megaItems = Array.from(nav.querySelectorAll('[data-mega-parent]')).map((parent) => ({
      parent,
      toggle: parent.querySelector('[data-mega-toggle]'),
      submenu: parent.querySelector('[data-mega-submenu]'),
    })).filter((item) => item.toggle && item.submenu);

    this.megaItems.forEach((item) => {
      const { toggle } = item;
      this.listen(toggle, 'click', () => {
        if (this.openMegaItem === item) {
          this.closeMegaMenu();
        } else {
          this.closeMegaMenu();
          this.openMegaItem = item;
          toggle.setAttribute('aria-expanded', 'true');
          item.submenu.setAttribute('aria-hidden', 'false');
          item.submenu.inert = false;
          item.submenu.classList.add('is-open');
        }
      });
    });

    this.listen(document, 'pointerdown', (event) => {
      if (this.openMegaItem && !this.openMegaItem.parent.contains(event.target)) this.closeMegaMenu();
    });
    this.listen(document, 'keydown', (event) => {
      if (event.key !== 'Escape' || !this.openMegaItem) return;
      event.preventDefault();
      const toggle = this.openMegaItem.toggle;
      this.closeMegaMenu();
      toggle.focus({ preventScroll: true });
    });
    this.listen(nav, 'focusout', () => {
      queueMicrotask(() => {
        if (!this.destroyed && this.openMegaItem && !this.openMegaItem.parent.contains(document.activeElement)) {
          this.closeMegaMenu();
        }
      });
    });
    this.listen(window, 'resize', () => {
      if (window.matchMedia('(max-width: 768px)').matches) this.closeMegaMenu();
    }, { passive: true });
  }

  closeMegaMenu() {
    if (!this.openMegaItem) return;
    const { toggle, submenu } = this.openMegaItem;
    toggle.setAttribute('aria-expanded', 'false');
    submenu.setAttribute('aria-hidden', 'true');
    submenu.inert = true;
    submenu.classList.remove('is-open');
    this.openMegaItem = null;
  }

  updateCartCount() {
    // Listen for cart updates published by the active cart integration.
    this.cartUpdatedHandler = (event) => {
      const countElement = document.querySelector('[data-cart-count]');
      if (countElement && event.detail?.cart) {
        countElement.textContent = event.detail.cart.item_count;
      }
    };
    this.listen(document, 'cart:updated', this.cartUpdatedHandler);
  }

  destroy() {
    this.destroyed = true;
    this.closeMegaMenu();
    this.dialogs.forEach((dialog) => dialog.destroy());
    this.setMobileHeaderInactive(false);
    this.dialogs = [];
    this.listeners.forEach(([target, type, handler, options]) => {
      target.removeEventListener(type, handler, options);
    });
    this.listeners = [];
  }
}

const headerInstances = new WeakMap();

function initializeHeaders(root = document) {
  const headers = [];
  if (root instanceof Element && root.matches('[data-header]')) headers.push(root);
  if (root.querySelectorAll) headers.push(...root.querySelectorAll('[data-header]'));

  headers.forEach((header) => {
    if (!headerInstances.has(header)) headerInstances.set(header, new Header(header));
  });
}

function destroyHeaders(root) {
  if (!(root instanceof Element)) return;
  const headers = [];
  if (root.matches('[data-header]')) headers.push(root);
  if (root.querySelectorAll) headers.push(...root.querySelectorAll('[data-header]'));

  headers.forEach((header) => {
    const instance = headerInstances.get(header);
    if (instance) {
      instance.destroy();
      headerInstances.delete(header);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeHeaders());
} else {
  initializeHeaders();
}

document.addEventListener('shopify:section:load', (event) => initializeHeaders(event.target));
document.addEventListener('shopify:section:unload', (event) => destroyHeaders(event.target));
})();
